import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Loader2, Sparkles, AudioLines, Eraser, ArrowUpRight } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { produceInNewsStudio } from "@/components/newsroom/produce";

const CATEGORIES = [
  { id: "music", label: "Music News" }, { id: "entertainment", label: "Entertainment" },
  { id: "local", label: "Local News" }, { id: "sports", label: "Sports" },
  { id: "weather", label: "Weather" }, { id: "traffic", label: "Traffic" },
  { id: "breaking", label: "Breaking News" }, { id: "headlines", label: "Headline News" },
];

const FORMATS = [
  { id: "f15", label: "15-Second", sub: "15s" }, { id: "f30", label: "30-Second", sub: "30s" },
  { id: "f60", label: "1-Minute", sub: "60s" }, { id: "f120", label: "2-Minute", sub: "120s" },
  { id: "top_of_hour", label: "Top of Hour", sub: "60s" }, { id: "breaking", label: "Breaking News", sub: "30s" },
  { id: "music_update", label: "Music Update", sub: "60s" }, { id: "entertainment_update", label: "Entertainment", sub: "60s" },
  { id: "weather_break", label: "Weather Break", sub: "30s" }, { id: "traffic_update", label: "Traffic Update", sub: "30s" },
];

export const AnchorStudioTab = ({ stationId, prefill }) => {
  const navigate = useNavigate();
  const [category, setCategory] = useState("music");
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState("female_news");
  const [script, setScript] = useState("");
  const [used, setUsed] = useState([]);
  const [busyFmt, setBusyFmt] = useState("");
  const [busyRender, setBusyRender] = useState(false);
  const [bulletins, setBulletins] = useState([]);

  useEffect(() => {
    api.get("/newsroom/anchor/voices").then((r) => setVoices(r.data || [])).catch(() => {});
    api.get("/newsroom/anchor/bulletins").then((r) => setBulletins(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { if (prefill) setScript(prefill); }, [prefill]);

  const formatScript = async (fmtId) => {
    setBusyFmt(fmtId);
    try {
      const r = await api.post("/newsroom/anchor/format-script", { format_id: fmtId, category, station_id: stationId });
      setScript(r.data.script);
      setUsed(r.data.used_stories || []);
      toast.success(`${r.data.format} script written from ${r.data.used_stories.length} real stories`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Script generation failed");
    } finally { setBusyFmt(""); }
  };

  const render = async () => {
    if (!script.trim()) return toast.error("Write or auto-generate a script first");
    setBusyRender(true);
    try {
      const r = await api.post("/newsroom/anchor/render", { voice_id: voiceId, script });
      setBulletins((p) => [r.data, ...p]);
      toast.success("Bulletin rendered");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Render failed");
    } finally { setBusyRender(false); }
  };

  const words = script.trim() ? script.trim().split(/\s+/).length : 0;

  return (
    <div data-testid="newsroom-anchor-tab">
      <h2 className="font-display text-xl font-bold text-ink-primary">AI News Anchor Studio</h2>
      <p className="text-xs text-ink-muted mb-6">Professional broadcast-quality audio bulletins — written only from your real stories</p>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">News Category</div>
          <div className="flex flex-wrap gap-2 mb-5">
            {CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setCategory(c.id)} data-testid={`anchor-cat-${c.id}`}
                className={`rounded-full px-3.5 py-1.5 text-xs transition ${category === c.id ? "bg-brand-amber text-black font-semibold" : "border border-line text-ink-secondary hover:text-ink-primary"}`}>
                {c.label}
              </button>
            ))}
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-4 mb-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">Format Script</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {FORMATS.map((f) => (
                <button key={f.id} onClick={() => formatScript(f.id)} disabled={!!busyFmt} data-testid={`anchor-format-${f.id}`}
                  className="rounded-lg border border-line bg-bg-elevated p-3 text-center hover:border-brand-amber/50 transition disabled:opacity-60">
                  {busyFmt === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mx-auto mb-1" /> : <Sparkles className="h-3.5 w-3.5 mx-auto mb-1 text-brand-amber" />}
                  <div className="text-[11px] font-semibold text-ink-primary">{f.label}</div>
                  <div className="text-[9px] font-mono text-ink-muted">{f.sub}</div>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-ink-muted mt-2">Click any format to auto-write a bulletin from your latest real {category} stories.</p>
          </div>

          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">Script Editor</div>
            <button onClick={() => { setScript(""); setUsed([]); }} data-testid="anchor-clear-btn"
              className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink-primary transition">
              <Eraser className="h-3 w-3" /> Clear
            </button>
          </div>
          <textarea value={script} onChange={(e) => setScript(e.target.value)} rows={9} data-testid="anchor-script-editor"
            placeholder="Paste your script here, or use Format Script above to auto-generate one from real stories…"
            className="w-full rounded-xl border border-line bg-bg-surface p-4 text-sm text-ink-primary placeholder:text-ink-muted font-mono focus:outline-none focus:border-brand-amber/50" />
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[10px] font-mono text-ink-muted">{words} words · ~{Math.round(words / 2.6)}s read</span>
            {used.length > 0 && <span className="text-[10px] text-ink-muted">Sources: {used.map((u) => u.source).join(", ")}</span>}
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button onClick={render} disabled={busyRender} data-testid="anchor-render-btn"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-orange-500 px-4 py-3 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60">
              {busyRender ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
              {busyRender ? "Rendering…" : "Quick Voice Preview"}
            </button>
            <button onClick={() => produceInNewsStudio(navigate, { script, category, title: `Newsroom — ${category} bulletin` })}
              data-testid="anchor-produce-studio-btn"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-brand-amber/50 bg-brand-amber/10 px-4 py-3 text-sm font-bold text-brand-amber hover:bg-brand-amber/20 transition">
              Produce in News Studio <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
          <p className="text-[10px] text-ink-muted mt-2" data-testid="anchor-studio-hint">
            Quick preview = plain voice render. For ducked music beds, branding liners &amp; MP3 export, produce in News Studio.
          </p>
        </div>

        <div className="space-y-5">
          <div className="rounded-xl border border-line bg-bg-surface p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">News Voice</div>
            <div className="space-y-2">
              {voices.map((v) => (
                <button key={v.id} onClick={() => setVoiceId(v.id)} data-testid={`anchor-voice-${v.id}`}
                  className={`w-full rounded-lg border p-3 text-left transition ${voiceId === v.id ? "border-brand-amber/50 bg-brand-amber/5" : "border-line hover:border-ink-muted/40"}`}>
                  <div className="text-sm font-semibold text-ink-primary">{v.label}</div>
                  <div className="text-[11px] text-ink-muted">{v.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="anchor-recent-bulletins">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3">Recent Bulletins</div>
            {bulletins.length === 0 ? (
              <div className="text-xs text-ink-muted py-4 text-center">
                <AudioLines className="h-6 w-6 mx-auto mb-1 opacity-40" /> No saved bulletins yet.
              </div>
            ) : (
              <div className="space-y-3">
                {bulletins.slice(0, 8).map((b) => (
                  <div key={b.asset_id}>
                    <div className="flex items-center gap-2 text-xs mb-1">
                      <span className="flex-1 truncate font-semibold text-ink-primary">{b.label}</span>
                      {b.duration_seconds && <span className="font-mono text-[10px] text-ink-muted">{b.duration_seconds}s</span>}
                    </div>
                    <audio controls preload="none" className="w-full h-8" src={`${API_BASE}/imaging/assets/${b.asset_id}/audio`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
